import * as fs from 'fs';
import * as path from 'path';
import { colors } from './const';
import { resolveProjectRoot } from './resolve-project-root';
import { runScaffoldValidation } from './scaffold-validation';
import { scanProjectSource, scanMatrixInventoryGap, partitionMatrixLayoutIssues, scanMatrixLayoutIssuesStrict, scanInteractionIssues } from './project-source-scan';
import { runCommandSync } from './utils/exec';
import {
  CFM_NEXT_STEP_ASK_DEPLOY,
  formatInput2HandoffBlocks,
  formatInput4DeployHandoffBlocks,
} from './workflow-prompts';
import { writeCoreVerifyPassed } from './workflow-state';
import { formatNpmInstallCommand, getMissingPackages } from './install-manifest';

const c = colors;

const FAST_REQUIRED_DEPS = ['@tanstack/react-query', '@dnd-kit/core'] as const;

export interface RunVerifyBuildOptions {
  projectRoot?: string;
  fast?: boolean;
}

function resolveVerifyScriptPath(projectRoot: string): string | null {
  const copied = path.join(projectRoot, 'survey-ui', 'verify-agent-build.sh');
  if (fs.existsSync(copied)) return copied;

  const sdkScript = path.join(
    __dirname,
    '..',
    '..',
    'templates',
    'docs',
    'survey-ui',
    'verify-agent-build.sh',
  );
  if (fs.existsSync(sdkScript)) return sdkScript;

  return null;
}

export function runVerifyBuildFast(projectRoot: string): number {
  console.log(`${c.bold}=== CFM SDK — verify-build --fast (speed tool) ===${c.reset}`);
  console.log(`${c.dim}Project: ${projectRoot}${c.reset}\n`);

  const scaffold = runScaffoldValidation(projectRoot);
  for (const warn of scaffold.warnings) {
    console.log(`${c.yellow}WARN:${c.reset} ${warn}`);
  }
  if (!scaffold.ok) {
    console.log(`${c.red}FAIL:${c.reset} Scaffold incomplete:`);
    for (const err of scaffold.errors) {
      console.log(`  - ${err}`);
    }
    console.log(`\n${c.dim}Fix: setup/index.md · Next: npx cfm-sdk verify-build --fast${c.reset}`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Scaffold checks passed (${scaffold.framework})`);

  const scan = scanProjectSource(projectRoot);
  if (scan.stubMatches.length) {
    console.log(`${c.red}FAIL:${c.reset} Stub placeholder text found:`);
    for (const m of scan.stubMatches) console.log(`  ${m}`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} No stub placeholder text`);

  if (scan.internalSdkImports.length) {
    console.log(`${c.red}FAIL:${c.reset} Internal SDK imports found:`);
    for (const m of scan.internalSdkImports) console.log(`  ${m}`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} No internal SDK imports`);

  if (scan.nextImports.length) {
    console.log(`${c.red}FAIL:${c.reset} Stale next/* imports found:`);
    for (const m of scan.nextImports) console.log(`  ${m}`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} No next/* imports in project source`);

  if (scan.introStartIssues.length) {
    console.log(`${c.red}FAIL:${c.reset} Intro Start gate issues:`);
    for (const m of scan.introStartIssues) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: phase-5-6-build-gate.md#intro-start-gate · Next: npx cfm-sdk verify-build --fast${c.reset}`,
    );
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Intro Start gate passed`);

  if (scan.endPageCtaIssues.length) {
    console.log(`${c.red}FAIL:${c.reset} End-page CTA filter issues:`);
    for (const m of scan.endPageCtaIssues) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: 15-end-page.md · import endPageCtas from SDK · Next: npx cfm-sdk verify-build --fast${c.reset}`,
    );
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} End-page CTA filter passed`);

  if (scan.matrixStatementIssues.length) {
    console.log(`${c.red}FAIL:${c.reset} Matrix statement label issues:`);
    for (const m of scan.matrixStatementIssues) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: 04-csat.md / 09-slider-matrix.md · shouldShowMatrixStatementLabels · Next: npx cfm-sdk verify-build --fast${c.reset}`,
    );
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Matrix statement labels passed`);

  const { blocking: matrixBlocking, nearMisses: matrixNearMisses } = partitionMatrixLayoutIssues(
    scan.matrixLayoutIssues,
  );
  for (const m of matrixNearMisses) {
    console.log(`${c.yellow}NEAR-MISS:${c.reset} ${m.slice('NEAR-MISS: '.length)}`);
  }
  if (matrixBlocking.length) {
    console.log(`${c.red}FAIL:${c.reset} Matrix layout structure issues:`);
    for (const m of matrixBlocking) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: layout-cookbook.md · agent-per-type-verification.md Format rows · Next: npx cfm-sdk verify-build --fast${c.reset}`,
    );
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Matrix layout structure passed`);

  if (scan.mcqOtherIssues.length) {
    console.log(`${c.red}FAIL:${c.reset} MCQ Other option issues:`);
    for (const m of scan.mcqOtherIssues) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: 02-radio.md#other-option-typed-input · Next: npx cfm-sdk verify-build --fast${c.reset}`,
    );
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} MCQ Other option gate passed`);

  const interactionIssues = scanInteractionIssues(projectRoot);
  if (interactionIssues.length) {
    console.log(`${c.red}FAIL:${c.reset} Interaction / format contract issues:`);
    for (const m of interactionIssues) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: field-binding-map.md · client-lib-folder.md · layout-cookbook/mcq.md · Next: npx cfm-sdk verify-interactions --from-fetch${c.reset}`,
    );
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Interaction contracts passed`);

  // Styling is authored freely in the docs-only model — hardcoded colors are expected.
  // The verify gate checks integration correctness (stubs, imports, intro gate), not styling.

  const missingDeps = getMissingPackages(projectRoot, [...FAST_REQUIRED_DEPS]);
  if (missingDeps.length) {
    console.log(`${c.red}FAIL:${c.reset} Missing bundle dependencies in package.json:`);
    for (const pkg of missingDeps) console.log(`  - ${pkg}`);
    const cmd = formatNpmInstallCommand(missingDeps);
    console.log(
      `\n${c.dim}Fix: npm install @explorer02/cfm-survey-sdk @tanstack/react-query react-icons @dnd-kit/core @dnd-kit/sortable @dnd-kit/utilities — or re-run npx cfm-sdk seed-phase5${c.reset}`,
    );
    if (cmd) console.log(`${c.dim}Quick: ${cmd}${c.reset}`);
    return 1;
  }
  console.log(`${c.green}OK:${c.reset} Bundle deps present in package.json`);

  const tsconfigPath = path.join(projectRoot, 'tsconfig.json');
  if (!fs.existsSync(tsconfigPath)) {
    console.log(`${c.yellow}WARN:${c.reset} tsconfig.json missing — skipping tsc --noEmit`);
  } else {
    console.log(`${c.dim}Running tsc --noEmit...${c.reset}`);
    const ok = runCommandSync('npx', ['tsc', '--noEmit'], { cwd: projectRoot });
    if (!ok) {
      console.log(`\n${c.red}FAIL:${c.reset} TypeScript check failed — fix errors above`);
      console.log(`${c.dim}Next: npx cfm-sdk verify-build --fast${c.reset}`);
      return 1;
    }
    console.log(`${c.green}OK:${c.reset} TypeScript check passed`);
  }

  console.log(
    `\n${c.green}Speed check passed${c.reset} — ${c.bold}NOT preview-ready.${c.reset}`,
  );
  console.log(`${c.dim}Still required after JWT:${c.reset}`);
  console.log(`${c.dim}  1. npx cfm-sdk sync-from-fetch${c.reset}`);
  console.log(`${c.dim}  2. Implement layout-tasks.md per §3c row${c.reset}`);
  console.log(`${c.dim}  3. layout-audit.md Broken = 0${c.reset}`);
  console.log(`${c.dim}  4. npx cfm-sdk verify-layout --from-fetch${c.reset}`);
  console.log(`${c.dim}  5. Visual smoke: matrix cells align under headers${c.reset}`);
  console.log(`Phase 6 still requires ${c.bold}npx cfm-sdk verify-build${c.reset}`);
  console.log(`\n${formatInput2HandoffBlocks()}\n`);
  return 0;
}

export function runVerifyBuildFull(projectRoot: string): number {
  console.log(`${c.bold}=== CFM SDK — verify-build (core) ===${c.reset}`);
  console.log(`${c.dim}Project: ${projectRoot}${c.reset}\n`);

  const scriptPath = resolveVerifyScriptPath(projectRoot);
  if (!scriptPath) {
    console.log(
      `${c.red}FAIL:${c.reset} verify-agent-build.sh not found — install the SDK package`,
    );
    return 1;
  }

  const ok = runCommandSync('bash', [scriptPath], { cwd: projectRoot });
  if (!ok) {
    console.log(`\n${c.dim}Fix: phase-5-6-build-gate.md §8${c.reset}`);
    return 1;
  }

  // Post-sync structural layout gate. This runs AFTER sync-from-fetch, so §3c must be
  // populated — enforce the per-type matrix layout structure here (the --fast run happens
  // before sync-from-fetch, where §3c is legitimately empty and these checks no-op).
  const inventoryGap = scanMatrixInventoryGap(projectRoot);
  if (inventoryGap.length) {
    console.log(`${c.red}FAIL:${c.reset} Matrix inventory not populated:`);
    for (const m of inventoryGap) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: npx cfm-sdk sync-from-fetch → fill implementation_plan.md §3c → npx cfm-sdk verify-build${c.reset}`,
    );
    return 1;
  }

  const scan = scanProjectSource(projectRoot);
  const layoutIssuesStrict = scanMatrixLayoutIssuesStrict(projectRoot);
  const { blocking: matrixBlocking, nearMisses: matrixNearMisses } = partitionMatrixLayoutIssues(
    layoutIssuesStrict,
  );
  for (const m of matrixNearMisses) {
    console.log(`${c.yellow}NEAR-MISS:${c.reset} ${m.slice('NEAR-MISS: '.length)}`);
  }
  const structural = [...scan.matrixStatementIssues, ...matrixBlocking, ...scanInteractionIssues(projectRoot)];
  if (structural.length) {
    console.log(`${c.red}FAIL:${c.reset} Matrix layout structure issues:`);
    for (const m of structural) console.log(`  ${m}`);
    console.log(
      `\n${c.dim}Fix: layout-cookbook.md · agent-per-type-verification.md · §3c variant per matrix question${c.reset}`,
    );
    return 1;
  }

  writeCoreVerifyPassed(projectRoot);
  console.log(
    `\n${c.dim}Structural spine OK — not layout-perfect. HOOK 3 requires ${c.bold}npx cfm-sdk verify-layout${c.reset} before preview.${c.reset}`,
  );
  console.log(`\n${CFM_NEXT_STEP_ASK_DEPLOY}`);
  console.log(`\n${formatInput4DeployHandoffBlocks()}\n`);
  return 0;
}

export function runVerifyBuild(options: RunVerifyBuildOptions = {}): number {
  const projectRoot = resolveProjectRoot(options.projectRoot);
  if (options.fast) {
    return runVerifyBuildFast(projectRoot);
  }
  return runVerifyBuildFull(projectRoot);
}
